import { useEffect, useMemo } from 'react';
import { Info } from 'lucide-react';
import {
  LinkBase,
  useCreatePath,
  useGetResourceLabel,
  useListContext,
  useTranslate,
} from 'ra-core';
import { DataTable } from '../../components/admin/data-table';
import { DateField } from '../../components/admin/date-field';
import { Error as ErrorPage } from '../../components/admin/error';
import { Loading } from '../../components/admin/loading';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { BulkUserDisableAction } from './BulkUserDisableAction';
import { createUserFilters } from './UserFilters';
import { UserToolbarActions } from './UserToolbarActions';
import { UserPictureField } from './UserPictureField';
import { Avatar, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { StringArrayBadgeField } from './StringArrayBadgeField';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { ResourcesValue } from '../resources';
import { useRestrictedUserUnits } from './useRestrictedUserUnits';
import { getAdminUserDisplayName } from './types';
import { saveSpreadsheet } from '../../utils/spreadsheet/save';
import { useCurrentUser } from '../../auth/currentUserContext';
import './UserList.css';

export interface UserListEntry {
  id: number;
  internalId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  pictureUrl?: string;
  startDate?: string;
  departmentName: string;
  jobTitleName: string;
  regionName: string;
  units: string[];
  activatedOn?: string;
  disabledFrom?: string;
  roles: string[];
}

export const userListExporter = async (users: UserListEntry[]) => {
  const columns = [
    // Match as close as possible to the CSV import template
    'id',
    'internalId',
    'firstName',
    'lastName',
    'phoneNumber',
    'email',
    'startDate',
    'jobTitle',
    'department',
    'region',
    'unit',
    'activatedOn',
    'disabledFrom',
    'roles',
  ] as const;

  await saveSpreadsheet({
    format: 'csv',
    data: users.map((user) => ({
      id: user.id,
      internalId: user.internalId,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
      email: user.email,
      department: user.departmentName,
      jobTitle: user.jobTitleName,
      region: user.regionName,
      unit: user.units.join(', '),
      startDate: user.startDate,
      activatedOn: user.activatedOn,
      disabledFrom: user.disabledFrom,
      roles: user.roles.join(', '),
    })),
    basename: ResourcesValue.USERS,
    columns: [...columns],
  });
};

export const UserList = () => {
  const { id: currentUserId } = useCurrentUser();
  const {
    hasAdminAccess,
    isUnitAdmin,
    restrictedUnitIds,
    isPending,
    isError,
    error,
    refetch,
  } = useRestrictedUserUnits();
  const isSmallScreen = useIsSmallScreen();
  const filters = useMemo(
    () => createUserFilters({ isSmallScreen, isAdmin: hasAdminAccess }),
    [isSmallScreen, hasAdminAccess]
  );

  if (isUnitAdmin) {
    if (isPending) return <Loading />;
    if (isError) {
      return (
        <ErrorPage error={error} resetErrorBoundary={() => void refetch()} />
      );
    }
  }

  return (
    <MoonstarList<UserListEntry>
      actions={<UserToolbarActions />}
      contentClassName="users-list-panel"
      filters={filters}
      filterDefaultValues={{
        userIsDisabled: false,
        ...(isUnitAdmin ? { unitIds: restrictedUnitIds } : {}),
      }}
      exporter={userListExporter}
      sort={{ field: 'id', order: 'ASC' }}
      resource={ResourcesValue.USERS}
    >
      <ExcludeCurrentUserFromSelection currentUserId={currentUserId} />
      {isSmallScreen ? (
        <UserMobileList />
      ) : (
        <DataTable<UserListEntry>
          bulkActionButtons={<BulkUserDisableAction />}
          // Shift-selection can pass undefined for selections on another page.
          isRowSelectable={(record: UserListEntry | undefined) =>
            record?.id !== currentUserId
          }
          rowClick="edit"
        >
          <DataTable.Col<UserListEntry>
            source="pictureUrl"
            disableSort
            className="w-px whitespace-nowrap"
          >
            <UserPictureField />
          </DataTable.Col>
          <DataTable.Col<UserListEntry>
            source="internalId"
            label="Internal ID"
          />
          <DataTable.Col<UserListEntry>
            source="firstName"
            render={(record) => (
              <span className="inline-flex items-center gap-2">
                <span>{record.firstName}</span>
                {record.id === currentUserId && <CurrentUserIndicator />}
              </span>
            )}
          />
          <DataTable.Col<UserListEntry> source="lastName" />
          <DataTable.Col<UserListEntry> source="phoneNumber" />
          <DataTable.Col<UserListEntry> source="email" />
          <DataTable.Col<UserListEntry> source="jobTitleName" />
          <DataTable.Col<UserListEntry> source="departmentName" />
          <DataTable.Col<UserListEntry> source="regionName" />
          <DataTable.Col<UserListEntry> source="units" disableSort>
            <StringArrayBadgeField source="units" />
          </DataTable.Col>
          <DataTable.Col<UserListEntry> source="activatedOn">
            <DateField source="activatedOn" />
          </DataTable.Col>
          <DataTable.Col<UserListEntry> source="disabledFrom">
            <DateField source="disabledFrom" />
          </DataTable.Col>
          <DataTable.Col<UserListEntry> source="roles" disableSort>
            <StringArrayBadgeField source="roles" />
          </DataTable.Col>
        </DataTable>
      )}
    </MoonstarList>
  );
};

const CurrentUserIndicator = () => {
  const translate = useTranslate();
  const label = translate('moonstar.user.currentUserLabel');
  const explanation = translate('moonstar.user.disable.selfDisableExplanation');

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              type="button"
              variant="secondary"
              size="xs"
              aria-label={`${label}: ${explanation}`}
              onClick={(event) => event.stopPropagation()}
            />
          }
        >
          {label}
          <Info aria-hidden />
        </TooltipTrigger>
        <TooltipContent>{explanation}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const ExcludeCurrentUserFromSelection = ({
  currentUserId,
}: {
  currentUserId: number;
}) => {
  const { selectedIds, onSelect } = useListContext<UserListEntry>();

  useEffect(() => {
    // Other user lists share the selection store and may allow selecting this user.
    if (selectedIds.includes(currentUserId)) {
      onSelect(selectedIds.filter((id) => id !== currentUserId));
    }
  }, [currentUserId, onSelect, selectedIds]);

  return null;
};

const UserMobileList = () => {
  const { data = [], isPending } = useListContext<UserListEntry>();
  const createPath = useCreatePath();
  const translate = useTranslate();
  const getResourceLabel = useGetResourceLabel();

  if (isPending) {
    return (
      <div className="flex flex-col gap-3 p-3">
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        {translate('ra.navigation.no_results', {
          name: getResourceLabel(ResourcesValue.USERS, 0),
        })}
      </p>
    );
  }

  return (
    <div className="flex flex-col divide-y rounded-md border">
      {data.map((record) => {
        const displayName = getAdminUserDisplayName(record);

        return (
          <LinkBase
            key={record.id}
            to={createPath({
              resource: ResourcesValue.USERS,
              id: record.id,
              type: 'edit',
            })}
            className="flex items-start gap-3 p-3 hover:bg-muted/50"
          >
            <Avatar className="size-10">
              <AvatarImage src={record.pictureUrl} alt="" />
            </Avatar>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{displayName}</span>
              {record.units.length > 0 && (
                <span className="mt-1 block text-sm text-muted-foreground">
                  {record.units.join(', ')}
                </span>
              )}
              {record.internalId && (
                <code className="mt-1 block text-xs text-muted-foreground">
                  {record.internalId}
                </code>
              )}
            </span>
          </LinkBase>
        );
      })}
    </div>
  );
};
